import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPinIcon, CalendarIcon, UsersIcon, ArrowRightIcon, TagIcon, ArchiveIcon, ChevronDownIcon } from 'lucide-react';
import { Button } from '../components/Button';
import { regionDatabase, getStatusStyles, isSessionPast } from '../data/bookingData';
export function RegionalBookingPage() {
  const {
    region
  } = useParams<{
    region: string;
  }>();
  const [activeFilter, setActiveFilter] = useState<string>('All Sports');
  const [showPastEvents, setShowPastEvents] = useState(false);
  const regionKey = region?.toLowerCase() || '';
  const data = regionDatabase[regionKey];
  if (!data) {
    return <main className="min-h-[70vh] flex items-center justify-center bg-navy-50 px-4 pt-20" data-id="element-1208">
        <div className="text-center max-w-lg" data-id="element-1209">
          <h1 className="text-4xl font-bold text-navy-900 mb-4" data-id="element-1210">
            Region Not Found
          </h1>
          <p className="text-lg text-navy-600 mb-8" data-id="element-1211">
            We couldn't find booking information for this region.
          </p>
          <Link to="/book" data-id="element-1212">
            <Button variant="primary" className="inline-flex items-center" data-id="element-1213">
              <ArrowRightIcon className="w-4 h-4 mr-2 rotate-180" data-id="element-1214" />
              Back to Locations
            </Button>
          </Link>
        </div>
      </main>;
  }
  const allSports = Array.from(new Set(data.venues.flatMap(venue => venue.sessions.filter(s => !isSessionPast(s)).map(session => session.sport)))).sort();
  // Count total past sessions across all venues
  const totalPastSessions = data.venues.reduce((count, venue) => count + venue.sessions.filter(s => isSessionPast(s)).length, 0);
  return <main className="bg-white pt-20" data-id="element-1215">
      {/* Intro Section */}
      <section className="relative overflow-hidden bg-brand-blue pt-16 pb-12 md:pt-20 md:pb-16" data-id="element-1216">
        <img src="https://cdn.magicpatterns.com/uploads/8m3ZDfLxFh7GqHfUAhVAku/Adults-Badminton-Photo-TK-LK-scaled-e1737371933423_(1).jpg" alt="" className="absolute inset-0 w-full h-full object-cover" aria-hidden="true" data-id="element-1217" />
        <div className="absolute inset-0 bg-brand-blue/85" data-id="element-1218"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1219">
          <nav aria-label="Breadcrumb" className="flex text-sm font-medium text-white/80 mb-6" data-id="element-1220">
            <Link to="/" className="hover:text-white transition-colors" data-id="element-1221">
              Home
            </Link>
            <span className="mx-2" data-id="element-1222">/</span>
            <Link to="/book" className="hover:text-white transition-colors" data-id="element-1223">
              Book Now
            </Link>
            <span className="mx-2" data-id="element-1224">/</span>
            <span className="text-white" data-id="element-1225">{data.name}</span>
          </nav>

          <div className="max-w-3xl" data-id="element-1226">
            <span className="block text-sm font-bold tracking-widest uppercase mb-3 text-brand-yellow" data-id="element-1227">
              Book Now
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-white mb-4 leading-tight uppercase" data-id="element-1228">
              {data.name} Workshops &amp; Classes
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed" data-id="element-1229">
              {data.description}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-24 bg-gray-50" data-id="element-1230">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1231">
          {/* Filter Row & Change Location */}
          <div className="mb-12 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-1232">
            {allSports.length > 1 ? <div className="flex flex-wrap gap-3 items-center" data-id="element-1233">
                <span className="text-sm font-bold text-navy-900 uppercase tracking-wider mr-2" data-id="element-1234">
                  Filter:
                </span>
                <button onClick={() => setActiveFilter('All Sports')} className={`px-4 py-2 text-sm font-medium rounded-md transition-colors border ${activeFilter === 'All Sports' ? 'bg-navy-900 text-white border-navy-900' : 'bg-white text-navy-600 border-gray-300 hover:border-navy-400'}`} data-id="element-1235">
                  All Sports
                </button>
                {allSports.map(sport => <button key={sport} onClick={() => setActiveFilter(sport)} className={`px-4 py-2 text-sm font-medium rounded-md transition-colors border ${activeFilter === sport ? 'bg-navy-900 text-white border-navy-900' : 'bg-white text-navy-600 border-gray-300 hover:border-navy-400'}`} data-id="element-1236">
                    {sport}
                  </button>)}
              </div> : <div data-id="element-1237" />}
            <Link to="/book" className="text-sm font-medium text-brand-red hover:text-red-800 underline underline-offset-4 transition-colors shrink-0" data-id="element-1238">
              Change location
            </Link>
          </div>

          {/* Venues and Listings */}
          <div className="space-y-10" data-id="element-1239">
            {data.venues.map(venue => {
            const filteredSessions = venue.sessions.filter(session => !isSessionPast(session) && (activeFilter === 'All Sports' || session.sport === activeFilter));
            if (filteredSessions.length === 0) return null;
            return <div key={venue.id} className="bg-white rounded-lg border-2 border-navy-200 overflow-hidden" data-id="element-1240">
                  {/* Venue Header */}
                  <div className="bg-navy-50 border-b border-gray-200 px-6 md:px-8 py-5" data-id="element-1241">
                    <h2 className="text-xl font-display font-bold text-navy-900 flex items-center" data-id="element-1242">
                      <MapPinIcon className="w-5 h-5 text-brand-red mr-3 shrink-0" data-id="element-1243" />
                      {venue.name}
                    </h2>
                    <p className="text-navy-500 text-sm mt-1 ml-8" data-id="element-1244">
                      {venue.address}
                    </p>
                  </div>

                  {/* Sessions List */}
                  <div className="divide-y divide-gray-100" data-id="element-1245">
                    {filteredSessions.map(session => <div key={session.id} className="px-6 md:px-8 py-5 flex flex-col md:flex-row md:items-center justify-between gap-5" data-id="element-1246">
                        {/* Session Info */}
                        <div className="flex-1" data-id="element-1247">
                          <div className="flex flex-wrap items-center gap-3 mb-2" data-id="element-1248">
                            <h3 className="text-base font-bold text-navy-900" data-id="element-1249">
                              {session.title}
                            </h3>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-navy-50 text-navy-700 border border-navy-100" data-id="element-1250">
                              {session.sport}
                            </span>
                          </div>
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 text-sm text-navy-600" data-id="element-1251">
                            <div className="flex items-center" data-id="element-1252">
                              <UsersIcon className="w-4 h-4 mr-2 text-navy-400" data-id="element-1253" />
                              {session.ageGroup}
                            </div>
                            <div className="flex items-center" data-id="element-1254">
                              <CalendarIcon className="w-4 h-4 mr-2 text-navy-400" data-id="element-1255" />
                              {session.dayTime}
                            </div>
                          </div>
                          {session.pricing && <div className="flex items-center text-sm font-semibold text-navy-900 mt-2" data-id="element-1256">
                              <TagIcon className="w-4 h-4 mr-2 text-brand-yellow" data-id="element-1257" />
                              {session.pricing}
                            </div>}
                        </div>

                        {/* Status and Action */}
                        <div className="flex flex-col sm:flex-row md:flex-col items-start sm:items-center md:items-end gap-3 md:w-44 shrink-0" data-id="element-1258">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusStyles(session.status)}`} data-id="element-1259">
                            {session.status}
                          </span>
                          <Button href={session.status === 'Sold out' ? undefined : `/book/${regionKey}/${session.id}`} variant={session.status === 'Sold out' ? 'secondary' : 'primary'} size="sm" className="w-full sm:w-auto md:w-full" disabled={session.status === 'Sold out'} data-id="element-1260">
                            {session.status === 'Join waiting list' ? 'Join Waitlist' : session.status === 'Sold out' ? 'Sold Out' : 'Book Now'}
                          </Button>
                        </div>
                      </div>)}
                  </div>
                </div>;
          })}

            {/* Empty state — no upcoming venues/sessions */}
            {data.venues.length === 0 || data.venues.every(v => v.sessions.filter(s => !isSessionPast(s)).length === 0) ? <div className="bg-white rounded-lg border-2 border-navy-200 px-6 md:px-8 py-12 text-center" data-id="element-1261">
                <h3 className="text-xl font-display font-bold text-navy-900 mb-3" data-id="element-1262">
                  No upcoming sessions available
                </h3>
                <p className="text-navy-600 mb-6 max-w-md mx-auto" data-id="element-1263">
                  We don't currently have any upcoming sessions in {data.name},
                  but we're always expanding. If you're interested in classes in
                  this area, please get in touch.
                </p>
                <Button onClick={() => document.getElementById('contact')?.scrollIntoView({
              behavior: 'smooth'
            })} variant="primary" data-id="element-1264">
                  Contact Us
                </Button>
              </div> : null}

            {/* Past Events Section */}
            {totalPastSessions > 0 && <div className="mt-12" data-id="element-1265">
                <button onClick={() => setShowPastEvents(!showPastEvents)} className="flex items-center gap-3 text-navy-500 hover:text-navy-700 transition-colors group mb-6" data-id="element-1266">
                  <ArchiveIcon className="w-5 h-5" data-id="element-1267" />
                  <span className="text-sm font-bold uppercase tracking-wider" data-id="element-1268">
                    Past Events ({totalPastSessions})
                  </span>
                  <ChevronDownIcon className={`w-4 h-4 transition-transform duration-300 ${showPastEvents ? 'rotate-180' : ''}`} data-id="element-1269" />
                </button>

                {showPastEvents && <div className="space-y-8 opacity-60" data-id="element-1270">
                    {data.venues.map(venue => {
                const pastSessions = venue.sessions.filter(s => isSessionPast(s));
                if (pastSessions.length === 0) return null;
                return <div key={`past-${venue.id}`} className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-1271">
                          <div className="bg-gray-50 border-b border-gray-200 px-6 md:px-8 py-4" data-id="element-1272">
                            <h3 className="text-lg font-display font-bold text-navy-700 flex items-center" data-id="element-1273">
                              <MapPinIcon className="w-4 h-4 text-navy-400 mr-3 shrink-0" data-id="element-1274" />
                              {venue.name}
                            </h3>
                          </div>
                          <div className="divide-y divide-gray-100" data-id="element-1275">
                            {pastSessions.map(session => <div key={session.id} className="px-6 md:px-8 py-4 flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-1276">
                                <div className="flex-1" data-id="element-1277">
                                  <div className="flex flex-wrap items-center gap-3 mb-1" data-id="element-1278">
                                    <h4 className="text-sm font-bold text-navy-700" data-id="element-1279">
                                      {session.title}
                                    </h4>
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-navy-500 border border-gray-200" data-id="element-1280">
                                      {session.sport}
                                    </span>
                                  </div>
                                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 text-sm text-navy-500" data-id="element-1281">
                                    <div className="flex items-center" data-id="element-1282">
                                      <CalendarIcon className="w-4 h-4 mr-2 text-navy-300" data-id="element-1283" />
                                      {session.dayTime}
                                    </div>
                                  </div>
                                </div>
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-navy-400 border border-gray-200" data-id="element-1284">
                                  Event ended
                                </span>
                              </div>)}
                          </div>
                        </div>;
              })}
                  </div>}
              </div>}
          </div>
        </div>
      </section>
    </main>;
}